import { AfterViewInit, Component, ElementRef, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, ViewChild } from '@angular/core';
import { IParticipant, IReportItem } from '@interfaces/interfaces';
import { SessionItemData } from '@workflow/services/session-item-data';
import { Subscription } from 'rxjs';
import { ActiveParticipantService } from '@app/services/participants/active-participant.service';
import { BfsGraphReportItem } from '@interfaces/reports';
import * as d3 from 'd3';
import { TranslateService } from '@ngx-translate/core';
import { select, Store } from '@ngrx/store';
import * as Reducers from '@app/store/reducers';
import { InterpolationService } from '@app/services/interpolation/interpolation.service';
import _ from 'lodash';

@Component({
	selector: 'workflow-graph-report',
	templateUrl: './bfs-graph-report.component.html',
	styleUrls: ['./bfs-graph-report.component.scss'],
})
export class BfsGraphReportComponent implements OnInit, OnDestroy, OnChanges, AfterViewInit {
	@Input() report: IReportItem;
	@Input() data: SessionItemData;
	@Input() containerWidth: number;

	private _bfsGraph: ElementRef<HTMLElement>;
	public bfsGraphReport: BfsGraphReportItem;
	participantActualAge: number;
	private participantBfAge: number;
	interpolatedBfAge: string;
	isBfaValueRestricted: boolean;
	optionalValueRestrictionAsterisk: '' | '*' = '';

	private isMale: boolean;
	private participant: IParticipant;
	private subscription: Subscription = new Subscription();

	private height = 400;
	private heightAdjust = 90;
	private width = 850;
	private yLabelAdjust = 50;
	private ageRange = [35, 90];
	private lineWidth = 1.5;
	private pathWidth = 2.5;
	private circleRadius = 7.5;
	private plotBFAFigure: boolean;
	private plotAgeFigure: boolean;

	private svg;
	private yearsWord: string;
	private ageIndexWord: string;
	bfAgeWord: string;
	bfAgeAbb: string;
	actualAgeWord: string;
	actualAgeAbb: string;
	private referencePopulationWord: string;
	private language: string;

	private xAxis: d3.ScaleLinear<number, number, never>;
	private yAxis;
	private xTicks: number;
	private yTicks: number;
	private ticksThreshold = 600;

	private biAge: number;

	private referenceGraph: number[][];
	private viewInit: boolean;

	colors = {
		referenceGraph: '#4b7cf3',
		actualAge: '#000000',
		bfAge: '#ee0000'
	};

	private bfaValueContext = {
		value: 0,
		language: 'en-US',
		numberFormat: `1.0-2`,
	};

	@ViewChild('bfsGraph')
	set bfsGraph(bfsGraph: ElementRef<HTMLElement>) {
		this._bfsGraph = bfsGraph;
	}

	get bfsGraph(): ElementRef<HTMLElement> {
		return this._bfsGraph;
	}

	constructor(
			private activeParticipant: ActiveParticipantService,
			private translateService: TranslateService,
			private store: Store,
			private interpolationService: InterpolationService
	) {
	}

	ngOnInit(): void {
		this.subscription.add(this.activeParticipant.participant$.subscribe(activeParticipant => {
			this.participant = activeParticipant;
			this.setParticipantData();
		}));
		this.subscription.add(this.translateService.stream('word.years').subscribe(translation => {
			this.yearsWord = translation;
			this.svg?.select('.x-label-years')
				.text(this.yearsWord);
		}));
		this.subscription.add(this.translateService.stream('word.ageIndex').subscribe(translation => {
			this.ageIndexWord = translation;
			this.svg?.select('.y-label-age-index')
				.text(this.ageIndexWord);
		}));
		this.subscription.add(this.translateService.stream('misc.actualAgeLabel').subscribe(translation => {
			this.actualAgeWord = translation;
			this.svg?.select('.age-label')
				.text(this.actualAgeWord);
		}));
		this.subscription.add(this.translateService.stream('misc.actualAgeAbbreviation').subscribe(translation => {
			this.actualAgeAbb = translation;
			this.svg?.select('.age-abb')
				.text(this.actualAgeAbb);
		}));
		this.subscription.add(this.translateService.stream('misc.bfaLabel').subscribe(translation => {
			this.bfAgeWord = translation;
			this.svg?.select('.bfa-label')
				.text(this.bfAgeWord);
		}));
		this.subscription.add(this.translateService.stream('misc.bfaAbbreviation').subscribe(translation => {
			this.bfAgeAbb = translation;
			this.svg?.select('.bfa-abb')
				.text(this.bfAgeAbb);
		}));
		this.subscription.add(this.translateService.stream('misc.referencePopulationLabel').subscribe(translation => {
			this.referencePopulationWord = translation;
			this.svg?.select('.reference-label')
				.text(this.referencePopulationWord);
		}));
		this.subscription.add(this.store.pipe(select(Reducers.getSettings)).subscribe(state => {
			this.language = state.locale;
			this.bfaValueContext.language = this.language;
			this.svg?.select('.bfa-value')
				.text(this.interpolationService.interpolate('{{ value | number: numberFormat : language }}', this.bfaValueContext));
		}));
		this.xTicks = (this.containerWidth < this.ticksThreshold) ? 4 : 8;
		this.yTicks = (this.containerWidth < this.ticksThreshold) ? 5 : 10;
		this.interpolatedBfAge = this.interpolationService.interpolate('{{ value | number: numberFormat : language }}', this.bfaValueContext);
	}

	ngAfterViewInit(): void {
		this.drawGraph();
		this.viewInit = true;
	}

	ngOnDestroy():void {
		this.subscription.unsubscribe();
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (changes.data || changes.report) {
			this.bfsGraphReport = this.report as BfsGraphReportItem;
			this.setParticipantData();

			this.participantActualAge = this.data?.getProperty(this.bfsGraphReport?.actualAgeKey);
			this.participantBfAge = this.data?.getProperty(this.bfsGraphReport?.bfaKey);
			this.isBfaValueRestricted = this.data?.getProperty(this.bfsGraphReport?.isValueRestrictedKey);
			this.optionalValueRestrictionAsterisk = this.isBfaValueRestricted ? '*' : '';
			this.calculatePlotStatus();

			if (this.participantBfAge > this.participantActualAge) {
				this.colors.bfAge = '#ee0000';
			} else if (this.participantBfAge < this.participantActualAge) {
				this.colors.bfAge = '#00ee00';
			} else {
				this.colors.bfAge = this.colors.actualAge;
			}
			this.bfaValueContext.value = this.participantBfAge;
			if (this.viewInit) {
				this.drawGraph();
			}
		}
		if (changes.containerWidth) {
			this.resize();
			this.redrawAxis();
		}
	}

	private calculatePlotStatus(): void {
		this.plotBFAFigure = _.inRange(this.participantBfAge, this.ageRange[0], this.ageRange[1] + 0.01);
		this.plotAgeFigure = _.inRange(this.participantActualAge, this.ageRange[0], this.ageRange[1] + 0.01);
	}

	private drawGraph() {
		if (this.referenceGraph) {
			this.biAge = this.computeBI(this.participantBfAge);
			this.createSvg();
			this.createAxis();
			this.drawAxis();
			this.drawAverageLine();
			this.drawParticipantResults();
			this.drawReferenceLegend();
		}
	}

	private computeBI(age: number): number {
		if (age === 0) {
			return age;
		}
		if (this.isMale) {
			return Math.exp((age - 107.51) / 51.464);
		} else {
			return Math.exp((age - 109.13) / 57.067);
		}
	}

	private setParticipantData() {
		if (this.participant && this.bfsGraphReport) {
			const participantData = new SessionItemData(this.participant.data);
			this.isMale = participantData.getProperty(this.bfsGraphReport.genderKey) === 'male';

			this.referenceGraph = [];
			for (let i = 1; i <= 100; i++) {
				this.referenceGraph.push([i, this.computeBI(i)]);
			}
		}
	}

	private createSvg(): void {
		if (this.svg) {
			d3.select(this.bfsGraph.nativeElement).select('svg').remove();
		}
		this.svg = d3.select(this.bfsGraph.nativeElement)
			.append('div')
			.classed('svg-container', true)
			.append('svg')
			.attr('preserveAspectRatio', 'xMinYMin meet')
			.attr('viewBox', [0, 0, this.width + 15, this.height + 135])
			.classed('svg-responsive', true)
			.append('g')	// <g> für axis/ticks erstellen
			.classed('stage-class', true);
	}

	private createAxis(): void {

		// Create the X-axis band scale
		this.xAxis = d3.scaleLinear()
			.domain(this.ageRange)
			.range([0, this.width - 50]);

		// Create the Y-axis band scale
		const sortedBi = this.referenceGraph.sort((d1, d2) => d2[1] - d1[1]);
		const minVisibleValue = (this.biAge * 1.2 < 0.8) ? 0.8 : (this.biAge * 1.2);
		const max = Math.round(Math.min(1, minVisibleValue) * 20) / 20;
		const min = Math.round((sortedBi[sortedBi.length-1][1] + Number.EPSILON) * 10) / 10;

		this.yAxis = d3.scaleLinear()
			.domain([max, min]) // max und min sind vertauscht, da die Skala absteigend notiert
			.range([this.height, 0]);

		//X-Axis Label
		this.svg.append('text')
			.attr('class', 'x-label-years')
			.attr('text-anchor', 'middle')
			.attr('x', (this.width / 2) + 20)
			.attr('y', 525)
			.attr('fill', 'currentColor')
			.text(this.yearsWord);

		//Y-Axis Label
		this.svg.append('text')
			.attr('class', 'y-label-age-index')
			.attr('text-anchor', 'middle')
			.attr('transform', `translate(${10},${(this.height / 2) + this.heightAdjust})rotate(-90)`)
			.attr('fill', 'currentColor')
			.text(this.ageIndexWord);
	}

	private drawAxis() {
		// Draw the X-axis on the DOM
		this.svg?.append('g')
			.classed('svg-xTicks', true)
			.attr('transform', `translate(${this.yLabelAdjust}, ${this.height + this.heightAdjust})`)
			.call(d3.axisBottom(this.xAxis).tickSizeInner(-this.height).ticks(this.xTicks))
			.selectAll('text')
			.attr('transform', 'translate(0, 7)')
			.style('text-anchor', 'middle');

		// Draw the Y-axis on the DOM
		this.svg?.append('g')
			.classed('svg-yTicks', true)
			.attr('transform', `translate(${this.yLabelAdjust},${this.heightAdjust})`)
			.call(d3.axisLeft(this.yAxis).ticks(this.yTicks));
	}

	private resize() {

		this.xTicks = (this.containerWidth < this.ticksThreshold) ? 4 : 8;
		this.yTicks = (this.containerWidth < this.ticksThreshold) ? 5 : 10;
	}

	private redrawAxis() {

		d3.select('.svg-xTicks').remove();
		d3.select('.svg-yTicks').remove();

		this.drawAxis();
	}

	private drawAverageLine(): void {
		//Draw Line
		const x = this.xAxis;
		const y = this.yAxis;

		// <g> für graph erstellen
		const svgResponsive = d3.select(this.bfsGraph.nativeElement).select('.svg-responsive');
		svgResponsive.append('g')
			.classed('graph-class', true)
			.append('path')
			.datum(this.referenceGraph.filter(d => d[0] >= this.ageRange[0] && d[0] <= this.ageRange[1]))
			.attr('fill', 'none')
			.attr('stroke', this.colors.referenceGraph)
			.attr('stroke-width', this.pathWidth)
			.attr('d', <any>d3.line()
				.x(function(d: any) { return x(d[0]); })
				.y(function(d: any) { return y(d[1]); })
			)
			.attr('transform', `translate(${this.yLabelAdjust},${this.heightAdjust})`);
	}

	private drawReferenceLegend(): void {
		//  <g> für Legend erstellen
		// Legend Background
		const legendClass = d3.select(this.bfsGraph.nativeElement)
			.select('.svg-responsive')
			.append('g')
			.classed('legend-class', true);

		legendClass.append('rect')
			.attr('class', 'reference-background')
			.attr('x', this.width - 305)
			.attr('y', this.heightAdjust+25)
			.attr('width', 230)
			.attr('height', 20)
			.attr('fill', 'white');

		// Legend Box
		legendClass.append('rect')
			.attr('class', 'reference-legend')
			.attr('x', this.width - 300)
			.attr('y', this.heightAdjust+30)
			.attr('width', 30)
			.attr('height', 10)
			.attr('fill', this.colors.referenceGraph);

		//Legend Label
		legendClass.append('text')
			.attr('class', 'reference-label')
			.attr('text-anchor', 'start')
			.attr('transform', `translate(${this.width-265},${this.heightAdjust+40})`)
			.attr('fill', 'currentColor')
			.text(this.referencePopulationWord);
	}

	private drawParticipantResults(): void{

		// default value of stroke-dasharray: 3
		// Wenn der Abstand zwischen beide ages < (circle-radius x 3) ist, (darin circle-teil (r x 2))
		// wird solid line gezeichnet
		function dashValue(aAge, bAge, r) {
			if (Math.abs(aAge - bAge) < r * 3) {
				return (r * 3);
			}
			else {
				return 3;
			}
		}
		
		//Draw colored Lines
		const graphClass = d3.select(this.bfsGraph.nativeElement).select('.graph-class');
		//Line from BFA to Actual Age - maximale Ausdehnung ist zwischen den Y-Achsen (links, rechts)
		if (this.plotBFAFigure) {
			
			graphClass.append('line')
				.style('stroke', this.colors.actualAge)
				.attr('stroke-width', this.lineWidth)
				.attr('stroke-dasharray', dashValue(this.xAxis(this.participantActualAge), this.xAxis(this.participantBfAge), this.circleRadius))
				.attr('x1', this.xAxis(Math.max(this.participantActualAge, this.ageRange[0])))
				.attr('y1', this.yAxis(this.biAge))
				.attr('x2', this.xAxis(Math.min(this.participantBfAge, this.ageRange[1])))
				.attr('y2', this.yAxis(this.biAge))
				.attr('transform', `translate(${this.yLabelAdjust},${this.heightAdjust})`);
		}

		// Line and Circle from Axis to Actual Age
		// only when visible (age >= minAge und <= maxAge)
		if (this.plotAgeFigure) {
			graphClass.append('line')
				.style('stroke', this.colors.actualAge)
				.attr('stroke-width', this.lineWidth)
				.attr('x1', this.xAxis(this.participantActualAge))
				.attr('y1', this.height)
				.attr('x2', this.xAxis(this.participantActualAge))
				.attr('y2', this.yAxis(this.biAge))
				.attr('transform', `translate(${this.yLabelAdjust},${this.heightAdjust})`);
			graphClass.append('circle')
				.attr('fill', this.colors.actualAge)
				.attr('stroke', 'none')
				.attr('cx', this.xAxis(this.participantActualAge))
				.attr('cy', this.yAxis(this.biAge))
				.attr('r', this.circleRadius)
				.attr('transform', `translate(${this.yLabelAdjust},${this.heightAdjust})`);
		}

		// Line, Pfeilspitze and Circle from Axis to BFA
		// only when visible (bfa >= minAge und <= maxAge)
		if (this.plotBFAFigure) {
			graphClass.append('line')
				.style('stroke', this.colors.bfAge)
				.attr('stroke-width', this.lineWidth)
				.attr('x1', this.xAxis(this.participantBfAge))
				.attr('y1', this.height)
				.attr('x2', this.xAxis(this.participantBfAge))
				.attr('y2', this.yAxis(this.biAge))
				.attr('transform', `translate(${this.yLabelAdjust},${this.heightAdjust})`);
			graphClass.append('circle')
				.attr('fill', this.colors.bfAge)
				.attr('stroke', 'none')
				.attr('cx', this.xAxis(this.participantBfAge))
				.attr('cy', this.yAxis(this.biAge))
				.attr('r', this.circleRadius)
				.attr('transform', `translate(${this.yLabelAdjust},${this.heightAdjust})`);
			//  Pfeilspitze
			const arrowLine = [
				[this.xAxis(this.participantBfAge)+8, this.height-15],
				[this.xAxis(this.participantBfAge), this.height],
				[this.xAxis(this.participantBfAge)-8, this.height-15]
			];
			graphClass.append('polyline')
				.style('stroke', this.colors.bfAge)
				.style('fill', this.colors.bfAge)
				.style('stroke-width', this.lineWidth)
				.style('stroke-linejoin', 'bevel')
				.attr('points', <any>arrowLine)
				.attr('transform', `translate(${this.yLabelAdjust},${this.heightAdjust})`);
		}
	}
}
